'use client';

import { useState, Suspense } from 'react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = createClient();

      // Use NEXT_PUBLIC_APP_URL if set, otherwise fall back to window.location.origin
      // This ensures the recovery link always points to the correct published app URL
      const appOrigin =
        process.env.NEXT_PUBLIC_APP_URL?.replace(/\/$/, '') ||
        (typeof window !== 'undefined' ? window.location.origin : '');

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim(),
        {
          redirectTo: `${appOrigin}/auth/update-password`,
        }
      );

      if (resetError) {
        setError(resetError.message);
        setLoading(false);
        return;
      }

      // Always show success message — never reveal if email exists
      setSubmitted(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur inattendue';
      setError(message);
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center">
                <span className="text-[#00d4c8] text-xs font-bold">R</span>
              </div>
              <span className="text-white font-semibold tracking-wide">RDV-PARTNERS</span>
            </div>
          </div>

          <div className="bg-[#00d4c8]/10 border border-[#00d4c8]/30 rounded-xl px-6 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#00d4c8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-white text-sm leading-relaxed">
              Si cette adresse correspond à un compte,<br />
              un lien de réinitialisation vous a été envoyé.
            </p>
          </div>

          <div className="mt-8 text-center">
            <Link href="/commercial/login" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">
              ← Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center">
              <span className="text-[#00d4c8] text-xs font-bold">R</span>
            </div>
            <span className="text-white font-semibold tracking-wide">RDV-PARTNERS</span>
          </div>
          <h1 className="text-2xl font-light text-white tracking-wide">Réinitialiser votre mot de passe</h1>
          <p className="text-gray-500 text-sm mt-2">Entrez votre adresse email pour recevoir un lien de réinitialisation</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-400 mb-2 tracking-wider uppercase">
              Adresse email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="votre@email.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-[#00d4c8]/50 focus:bg-white/8 transition-all"
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full py-3 px-6 bg-[#00d4c8] text-[#0a0a0f] font-semibold rounded-lg hover:bg-[#00d4c8]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#0a0a0f]/30 border-t-[#0a0a0f] rounded-full animate-spin" />
                Envoi en cours…
              </span>
            ) : (
              'Recevoir le lien de réinitialisation'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link href="/commercial/login" className="text-gray-600 hover:text-gray-400 text-xs transition-colors">
            ← Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ForgotPasswordPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#00d4c8]/30 border-t-[#00d4c8] rounded-full animate-spin" />
      </div>
    }>
      <ForgotPasswordForm />
    </Suspense>
  );
}
